import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  DependencyList,
  ComponentType,
  FC,
  SetStateAction,
  Dispatch,
  ReactNode,
} from 'react';
import { IAppSettings, IConfigDto, Role } from 'interfaces';
import { debounce, isBoolean, isEqual, isFunction, isNil, isString, max, sortBy, uniqBy, ValueIteratee } from 'lodash';
import * as ReactIs from 'react-is';
import { breakPoints } from 'styling/stylingVars';
import { useHistory, useLocation } from 'react-router';
import memoize from 'memoize-one';
import { addNotification } from 'utils/NotificationManager';
import base64url from "base64url";
import FileSaver from 'file-saver';
import { EventEmitter } from 'events';
export { getServerUrl } from './browserSafeUtils'

export type Awaited<T> = T extends PromiseLike<infer U>
  ? { 0: Awaited<U>; 1: U }[U extends PromiseLike<any> ? 0 : 1]
  : T

export type MaybePromise<T> = Promise<T> | T;

export type Exact<A, B> = A extends B ? (B extends A ? A : never) : never;

const _emptyList: any[] = []
const _emptyObject: any = {}
export function emptyList<T>() { return _emptyList as T[] }
export function emptyObject<T>() { return _emptyObject as T }

export class ArrayWindow<T> {
  private data: T[];
  public start: number;
  public end: number;
  public length: number;

  constructor(data: T[], start: number, end: number) {
    this.data = data;
    this.start = start;
    this.end = end;
    this.length = end - start;
  }

  get = (index: number): T => {
    return this.data[this.start + index];
  };
}

export class ExtendedError extends Error {
  cause: Error;
  payload: any;

  constructor(message: string, payload: any, cause: Error) {
    super(message);
    this.name = 'ExtendedError';
    this.payload = payload;
    this.cause = cause;
  }
}

export class ControllerComponent<P = {}, S = {}, SS = any> extends React.Component<P, S, SS> {
  setStateAsync =
    <K extends keyof S>() =>
    (state: ((prevState: Readonly<S>, props: Readonly<P>) => Pick<S, K> | S | null) | (Pick<S, K> | S | null)) =>
      new Promise((resolve: any) => this.setState(state, resolve));
}

export function isWatchDevice() {
  return window.innerWidth <= 502 && window.innerHeight <= 410
}

export function useIsWatchDevice() {
  const [isWatch, setIsWatch] = useState(() => isWatchDevice())
  useEffect(() => {
    const handleResize = debounce(() => {
      setIsWatch(isWatchDevice())
    }, 250);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return isWatch
}

export function measureScrollbarWidth() {
  // Add temporary box to wrapper
  const scrollbox = document.createElement('div');
  scrollbox.style.overflow = 'scroll';
  document.body.appendChild(scrollbox);
  const scrollBarWidth = scrollbox.offsetWidth - scrollbox.clientWidth;
  document.body.removeChild(scrollbox);
  return scrollBarWidth;
}

export function saveBase64UrlEncode(s: string) {
  return base64url.encode(`${s}`)
}

export function renderIf<P>(C: React.ComponentType<P>, props: P & Record<string, any>) {
  if (!C) {
    return null
  }
  return <C {...props}/>
}

export interface KeyboardAbstract {
  ctrl: boolean;
  cmd: boolean;
  shift: boolean;
  alt: boolean;
  key: string;
}
export function getKeyboardEventAbstract(e: React.KeyboardEvent): KeyboardAbstract {
  return {
    ctrl: e.ctrlKey,
    cmd: e.metaKey,
    shift: e.shiftKey,
    alt: e.altKey,
    key: isAlphanumericKey(e) ? String.fromCharCode(e.keyCode).toUpperCase() : '',
  };
}

export function keyboardAbstractToString(e: KeyboardAbstract): string {
  let s = [];
  if (e.ctrl) s.push('⌃');
  if (e.cmd) s.push('⌘');
  if (e.alt) s.push('⌥');
  if (e.shift) s.push('⇧');
  if (e.key) s.push(e.key);
  return s.join(' + ');
}

export function isAlphanumericKey(e: React.KeyboardEvent): boolean {
  return (e.keyCode >= 48 && e.keyCode <= 57) || (e.keyCode >= 65 && e.keyCode <= 90);
}

export function WithLocationData<T>(Inner: ComponentType<{ request: T }>, ifMissing: string): FC<{}> {
  return (props) => {
    const location = useLocation(); // eslint-disable-line react-hooks/rules-of-hooks
    const history = useHistory(); // eslint-disable-line react-hooks/rules-of-hooks
    const request = location.state as T;

    if (!request) {
      history.push(ifMissing);
      return null;
    }

    return <Inner {...props} request={request} />;
  };
}

export function webpBase64ToPngBase64(webpBase64: string) {
  return new Promise<string>((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;

      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);

      // Export as PNG base64
      const pngBase64 = canvas.toDataURL("image/png");
      resolve(pngBase64.slice(pngBase64.indexOf(',') + 1));
    };
    img.onerror = reject;
    img.src = "data:image/webp;base64," + webpBase64;
  });
}

export function gifBase64ToPngBase64(gifBase64: string) {
  return new Promise<string>((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;

      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);

      // Export as PNG base64
      const pngBase64 = canvas.toDataURL("image/png");
      resolve(pngBase64.slice(pngBase64.indexOf(',') + 1));
    };
    img.onerror = reject;
    img.src = "data:image/gif;base64," + gifBase64;
  });
}
(window as any).gifBase64ToPngBase64 = gifBase64ToPngBase64;

export async function bufferToBase64(buffer: Uint8Array) {
  // use a FileReader to generate a base64 data URI:
  const base64url: string = await new Promise(r => {
    const reader = new FileReader()
    reader.onload = () => r(reader.result as string)
    reader.readAsDataURL(new Blob([buffer]))
  });
  // remove the `data:...;base64,` part from the start
  return base64url.slice(base64url.indexOf(',') + 1);
}

export function readFileAsync(file: File): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    let reader = new FileReader();
    reader.onload = () => {
      resolve(reader.result as ArrayBuffer);
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
}

export function readFileBase64Async(file: File | Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    let reader = new FileReader();
    reader.onload = () => {
      const res = reader.result as string
      resolve(res.substring(res.indexOf(',')+1));
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function readTextFileAsync(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    let reader = new FileReader();
    reader.onload = () => {
      resolve(reader.result as string);
    };
    reader.onerror = reject;
    reader.readAsText(file);
  });
}

export function readImage(img: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const base_image = new Image();
    base_image.src = img;
    base_image.onload = function () {
      resolve(base_image);
    };
  });
}

export function firstNotNil<T>(x: T[]): T {
  return x.find(it => !isNil(it))
}

export function toAscii(str: string) {
  return str
    .replace(/ß/g, "ss")
    .replace(/æ/g, "ae")
    .replace(/Æ/g, "AE")
    .replace(/œ/g, "oe")
    .replace(/Œ/g, "OE")
    .replace(/ø/g, "o")
    .replace(/Ø/g, "O")
    .replace(/ł/g, "l")
    .replace(/Ł/g, "L")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\x00-\x7F]/g, ""); // eslint-disable-line no-control-regex
}

export function toBase64(original: string) {
  return Buffer.from(original, "utf8").toString("base64");
}

export function parseBase64(encoded: string) {
  return Buffer.from(encoded, "base64").toString("utf8");
}

export function ifnnf<T, R>(it: T, f: (it: T) => R) {
  if (isNil(it) || (isBoolean(it) && !it) || (isString(it) && !it)) {
    return null;
  }
  return f(it);
}

export function klet<T, R>(it: T, f: (it: T) => R) {
  if (isNil(it)) {
    return null;
  }
  return f(it);
}

export function useOnVisibilityChange(f: (isVisible: boolean) => any) {
  useEffect(() => {
    const df = () => {
      f(document.visibilityState === 'visible')
    }
    document.addEventListener('visibilitychange', df)
    return () => {
      document.removeEventListener('visibilitychange', df)
    }
  }, [f])
}

export function switche<T extends string|number, R>(v: T, cases: {[k in T]: () => R}) {
  return cases[v]?.() ?? null
}

export async function retryForOptimisticLocking<T>(retries: number, f: () => MaybePromise<T>) {
  let tries = 0
  while (retries === 0 || tries < retries) {
    try {
      return await f();
    } catch (e) {
      if (e.isAxiosError && e.response.status === 412) {
        await sleep(500);
      } else {
        throw e;
      }
    }
    tries += 1;
  }
  throw new Error("Too many optimistic locking exceptions, giving up")
}

export function nullable<T>(v: T): T {
  return isNil(v) ? null : v
}

export async function mapAsync<T, R>(iterable: T[], f: (it: T, index: number) => MaybePromise<R>): Promise<R[]> {
  const result: R[] = new Array(iterable.length);
  for (let index = 0; index < iterable.length; index++) {
    result[index] = await f(iterable[index], index);
  }
  return result;
}

export function useMemoized<F>(f: F): F {
  return useRefInitOnce(() => memoize(f as any)).current as unknown as F;
}

export function check(cond: any, error: () => ReactNode) {
  if (!cond) {
    addNotification('danger', error());
    throw new Error('check failed');
  }
}

export function performBase64Download(name: string, payload: string) {
  const data = Uint8Array.from(
    atob(payload as any).split('').map(char => char.charCodeAt(0))
  );  
  const file = new Blob([data]);
  FileSaver.saveAs(file, name);
}

export async function urlToBase64(url: string) {
  const req = await fetch(url)
  const blob = await req.blob()
  let data = await new Promise<string>(resolve => {
    var reader = new FileReader();
    reader.onload = function() { 
      return resolve((this.result as string).split(',')[1])
    };
    reader.readAsDataURL(blob);
  })
  return data
}

export async function base64ToPdfBase64(data: string, path: string) {
  // convert the image to a pdf on the fly
  if (
    path.endsWith(".png") || 
    path.endsWith(".jpg") || 
    path.endsWith(".jpeg") || 
    path.endsWith(".gif") || 
    path.endsWith(".webp")
  ) {
    const PDFLib = await import("pdf-lib")
    const pdfDoc = await PDFLib.PDFDocument.create();

    let image;
    if (path.endsWith(".png")) {
      image = await pdfDoc.embedPng(data);
    } else if (path.endsWith(".webp")) {
      image = await pdfDoc.embedPng(await webpBase64ToPngBase64(data));
    } else if (path.endsWith(".gif")) {
      image = await pdfDoc.embedPng(await gifBase64ToPngBase64(data));
    } else {
      image = await pdfDoc.embedJpg(data);
    }
    const width = image.width;
    const height = image.height;

    // Add page sized to image
    const page = pdfDoc.addPage([width, height]);
    page.drawImage(image, { x: 0, y: 0, width, height });
    const pdfBytes = await pdfDoc.save();
    data = await bufferToBase64(pdfBytes)
  }
  return data
}

export function isNumeric(input: any) {
  const n = parseFloat(input);
  return !isNaN(n) && isFinite(n);
}

export function getFunVarResult<T>(input: T | ((...x: any[]) => T), ...args: any[]) {
  if (isFunction(input)) {
    return input(...args);
  }
  return input;
}

export function associate<T, V>(
  arr: T[],
  mapper: (value: T, index: number, ...args: any[]) => [string | number, V],
  ...args: any[]
) {
  var rv: { [k: string]: V } = {};
  for (var i = 0; i < arr.length; ++i) {
    const [k, v] = mapper(arr[i], i, ...args);
    rv[k] = v;
  }
  return rv;
}

export function expandUserRoles(roles: Role[], config: IConfigDto) {
  const combinedUserRoles = roles.map((r) => (config.roleHierarchy[r] || []) as Array<Role>).flat();
  return Array.from(new Set(combinedUserRoles));
}

export function removeDoubles<T>(input: T[]) {
  return distinct(input);
}

export function filterBoth<T>(input: T[], f: (it: T, index: number) => boolean): [T[], T[]] {
  const rTrue = [];
  const rFalse = [];
  for (let index = 0; index < input.length; index++) {
    const element = input[index];
    if (f(element, index)) {
      rTrue.push(element);
    } else {
      rFalse.push(element);
    }
  }
  return [rTrue, rFalse];
}

export function getTruthy<T>(l: T[]) {
  return l.filter(it => it)
}

export function insertBetween<T, R>(l: T[], el: R) {
  return [].concat(...l.map(n => [n, el])).slice(0, -1)
}

export function safeParseFloat(x: any, defaultValue?: number) {
  const v = parseFloat(x)
  if (isNaN(v)) {
    if (!isNil(defaultValue)) {
      return defaultValue
    }
    throw new Error("invalid float value")
  }
  return v
}

export function withTimeout<T>(prom: Promise<T>, ms: number) {
  return Promise.race([prom, new Promise((_r, rej) => setTimeout(rej, ms))]);
}

export function listEqualsIgnoreOrder<T>(l1: T[], l2: T[]) {
  return isEqual(sortBy(l1), sortBy(l2));
}

export function distinct<T>(input: T[]) {
  return Array.from(new Set(input));
}

export function distinctBy<T>(input: T[], iteratee: ValueIteratee<T>) {
  return uniqBy(input, iteratee);
}

export function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function run<T>(f: () => T) {
  return f()
}

export function mapNotNil<T, R>(a: Array<T>, f: (x: T, i: number) => R): Array<R> {
  return a.map(f).filter((x) => !isNil(x));
}

export function mapNotNull<T, R>(a: Array<T>, f: (x: T, i: number) => R): Array<R> {
  return a.map(f).filter((x) => x !== null);
}

export function listOfNotNil<T>(...args: T[]) {
  return args.filter((x) => !isNil(x))
}

export function notNull<TValue>(value: TValue | null | undefined): value is TValue {
  return value !== null && value !== undefined;
}

export function groupBy<T>(xs: T[], key: keyof T | ((x: T) => any)): { [k: string]: T[] } {
  return xs.reduce(function (rv: any, x) {
    if (isFunction(key)) {
      (rv[key(x)] = rv[key(x)] || []).push(x);
    } else {
      (rv[x[key]] = rv[x[key]] || []).push(x);
    }
    return rv;
  }, {});
}

export function count<T>(xs: T[], iteratee: (v: T, index: number) => any): number {
  return xs.reduce(function (total, curr, index) {
    return total + (iteratee(curr, index) ? 1 : 0);
  }, 0);
}

export function cast<T = any>(x: any): T {
  return x;
}

export function allEqual<T>(x: T[]) {
  return x.every(v => isEqual(v, x[0]));
}

export function checkNotNull<T>(x: T, msg: ReactNode) {
  if (isNil(x)) {
    throw addNotification('danger', msg)
  }
  return x
}

export function toggle<T>(array: T[], value: T) {
  if (array.includes(value)) {
    return array.filter(it => it !== value)
  } else {
    return [...array, value]
  }
}

export function argmax<T>(array: T[]) {
  return array.map((x, i) => [x, i]).reduce((r, a) => (a[0] > r[0] ? a : r))[1];
}

export function argmin<T>(array: T[]) {
  return array.map((x, i) => [x, i]).reduce((r, a) => (a[0] < r[0] ? a : r))[1];
}

export function indexFilter<T>(indexes: number[]) {
  return (v: T, index: number) => !indexes.includes(index);
}

export function itemFilter<T>(items: T[]) {
  return (v: T) => !items.includes(v);
}

export function arraymove<T>(arr: T[], fromIndex: number, toIndex: number): T[] {
  const newArray = [...arr]
  const element = newArray[fromIndex];
  newArray.splice(fromIndex, 1);
  newArray.splice(toIndex, 0, element);
  return newArray
}

export function arrayinsert<T>(arr: T[], index: number, element: T): T[] {
  const newArray = [...arr]
  newArray.splice(index, 0, element);
  return newArray
}

export function strcmpCaseInsensitive(s1: string, s2: string) {
  return s1.toLowerCase() === s2.toLowerCase();
}

export function getPlusOrMinusScan(s: string, settings: IAppSettings) {
  let matches = new RegExp('^\\[PLUS(\\d+\\.?\\d*)\\]$', 'gi').exec(s);
  if (matches && matches[1] && !isNaN(parseFloat(matches[1]))) {
    return parseFloat(matches[1]);
  }

  matches = new RegExp('^\\[MIN(\\d+\\.?\\d*)\\]$', 'gi').exec(s);
  if (matches && matches[1] && !isNaN(parseFloat(matches[1]))) {
    return -parseFloat(matches[1]);
  }

  return false;
}

export function arrayIsSorted(array: any[], asc: boolean) {
  for (let i = 0; i < array.length - 1; i++) {
    if (asc ? array[i] > array[i + 1] : array[i] < array[i + 1]) {
      return false;
    }
  }
  return true;
}

export function getRenderable(Comp: any, props: any = {}, fallback = Comp) {
  if (ReactIs.isElement(Comp)) {
    return Comp;
  } else if (ReactIs.isValidElementType(Comp)) {
    return <Comp {...props} />;
  } else {
    return fallback;
  }
}

export function mobileAndTabletCheck() {
  let check = false;
  // prettier-ignore
  (function(a) {
    // eslint-disable-next-line no-useless-escape
    if(/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i.test(a)||/1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0,4))) check = true;
  })(navigator.userAgent || navigator.vendor || ((window as any).opera));
  return check;
}

export function useWindowSize() {
  function getSize() {
    const content = document.getElementById('application-content');
    return {
      width: content?.clientWidth || window.innerWidth,
      height: content?.clientHeight || window.innerHeight,
    };
  }

  const [windowSize, setWindowSize] = useState(getSize);
  useEffect(() => {
    const handleResize = debounce(() => {
      setWindowSize(getSize());
    }, 250);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []); // Empty array ensures that effect is only run on mount and unmount
  return windowSize;
}

export function useBreakpointWatcher(): keyof typeof breakPoints {
  function getBreakpoint() {
    const size = document.getElementById('application-content')?.clientWidth || window.innerWidth;
    const keys = Object.keys(breakPoints) as (keyof typeof breakPoints)[];
    const value = max(keys.map((it) => breakPoints[it]).filter((it) => it <= size));
    const bp = keys.find((it) => breakPoints[it] === value);
    return bp;
  }

  const [breakpoint, setBreakpoint] = useState(getBreakpoint);
  useEffect(() => {
    const handleResize = debounce(() => {
      setBreakpoint(getBreakpoint);
    }, 100);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return breakpoint;
}

export function useEffectAsync(f: () => Promise<void>, deps: DependencyList, cleanup = () => {}) {
  useEffect(() => {
    f();
    return cleanup;
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
}

export function useQuery() {
  const { search } = useLocation();

  return React.useMemo(() => new URLSearchParams(search), [search]);
}

export function useEventEmitter(emitter: EventEmitter, event: string, f: () => any) {
  useEffect(() => {
    if (!emitter) {
      return
    }

    emitter.on(event, f)
    return () => {
      emitter.off(event, f)
    }
  }, [emitter, event, f])
}

export function useStable<T>(val: T, deepComparison: boolean = true): [T, boolean, number] {
  const prevRef = useRef<T>(null);
  const versionRef = useRef<number>(0);

  // check whether the object changed
  let changed = val !== prevRef.current;
  if (deepComparison) {
    changed = !isEqual(val, prevRef.current);
  }

  // if the object changed we need to update the current reference and version id
  if (changed) {
    prevRef.current = val;
    versionRef.current += 1;
  }
  return [prevRef.current, changed, versionRef.current];
}

export function useConstantRef<T>(val: T) {
  const ref = useRef(val);
  ref.current = val;
  return ref;
}

export function useRerender() {
  const afterRenderListeners = useRef<any[]>([])
  const [, update] = useState(0);
  const rerender = useCallback((afterRender?: any) => {
    if (afterRender) afterRenderListeners.current.push(afterRender)
    update((old) => old + 1)
  }, [update]);
  useEffect(() => {
    afterRenderListeners.current.forEach(it => it())
    afterRenderListeners.current = []
  })
  return rerender;
}

export type StateSetter<T> = (value: T | ((old: T) => T)) => Promise<unknown>
export function useConsistentState<T>(
  init: T,
): [T, React.MutableRefObject<T>, StateSetter<T>] {
  const [state, setState] = useState<T>(init);
  const valueRef = useRef<T>(init);
  const resolverRef = useRef([]);

  // actually resolve the change
  useEffect(() => {
    // only notify of the latest update
    if (state === valueRef.current) {
      resolverRef.current.forEach((it) => it());
      resolverRef.current = [];
    }
  }, [state]);

  // add methods to set the state value
  const setValue = useCallback(
    (value: T | ((old: T) => T)) => {
      const resolved = getFunVarResult(value, valueRef.current);
      valueRef.current = resolved;
      const renderedChanges = new Promise((resolve) => { resolverRef.current.push(resolve); })
      setState(resolved);
      return renderedChanges;
    },
    [valueRef, setState],
  );

  return [state, valueRef, setValue];
}

export function useRefList<T>(size: number): [React.MutableRefObject<T[]>, (index: number) => any] {
  const refs = useRef(Array<T>(0).fill(null));
  if (size > refs.current.length) {
    refs.current = refs.current.concat(Array<T>(size - refs.current.length).fill(null));
  }
  return [
    refs,
    (index: number) => (e: T) => {
      refs.current[index] = e;
    },
  ];
}

export function useRefInitOnce<T>(init: () => T) {
  const instance = useRef<T>(null);
  if (instance.current == null) {
    instance.current = init();
  }
  return instance;
}

export function usePrevious<T>(value: T, defaultValue: T) {
  const ref = useRef<T>(defaultValue);
  const prev = ref.current;
  ref.current = value;
  return prev;
}

export function useLocalStorage<T>(key: string, initialValue: T, synchronised: boolean = true): [T, (value: T | ((old: T) => T)) => void] {
  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      // Get from local storage by key
      const item = window.localStorage.getItem(key);
      // Parse stored json or if none return initialValue
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      // If error also return initialValue
      console.log(error);
      return initialValue;
    }
  });

  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to localStorage.
  const setValue = useCallback(
    (value: T) => {
      try {
        // Save state
        setStoredValue((oldValue) => {
          // Allow value to be a function so we have same API as useState
          const valueToStore = value instanceof Function ? value(oldValue) : value;

          // Save to local storage
          window.localStorage.setItem(key, JSON.stringify(valueToStore));

          // set the new state
          return valueToStore;
        });
      } catch (error) {
        // A more advanced implementation would handle the error case
        console.log(error);
      }
    },
    [key],
  );

  useEffect(() => {
    if (!synchronised) return;

    const f = (e: StorageEvent) => {
      if (e.key === key) {
        setStoredValue(JSON.parse(e.newValue))
      }    
    }
    
    window.addEventListener('storage', f)
    return () => {
      window.removeEventListener('storage', f)
    }
  }, [key, setStoredValue, synchronised])

  return [storedValue, setValue];
}

export function HooksHoc<ExpectedProps extends object, Supplied extends object>(
  C: React.ComponentType<ExpectedProps>,
  f: () => Supplied,
): React.FC<Omit<ExpectedProps, keyof Supplied>> {
  return (props: object) => {
    const res = f();
    return <C {...(props as ExpectedProps)} {...res} />;
  };
}

export function RHooksHoc<ExpectedProps extends object, Supplied extends object>(
  f: () => Supplied,
  C: React.ComponentType<ExpectedProps>,
): React.FC<Omit<ExpectedProps, keyof Supplied>> {
  return HooksHoc(C, f);
}

export function retryable<T>(
  f: () => T | Promise<T>,
  interval: number | ((tried: number) => number),
): () => Promise<T> {
  return () =>
    new Promise((resolve) => {
      let tries = 1;
      const retry = async () => {
        try {
          const res = await f();
          resolve(res);
        } catch (e) {
          if (isFunction(interval)) {
            setTimeout(retry, interval(tries++));
          } else {
            setTimeout(retry, interval);
          }
        }
      };
      retry();
    });
}

export function dragDetect(maxTime: number, minHeightFraction: number, onDrag: (dir: 'up' | 'down') => void) {
  let sx: number, sy: number, st: Date;
  const touchStart = (e: TouchEvent) => {
    sx = e.touches[0].clientX;
    sy = e.touches[0].clientY;
    st = new Date();
  };
  const touchEnd = (e: TouchEvent) => {
    const ex = e.changedTouches[0].clientX;
    const ey = e.changedTouches[0].clientY;
    const dx = ex - sx,
      dy = ey - sy;
    const totalHeight = window.screen.height;
    const seconds = (new Date().getTime() - st.getTime()) / 1000;
    const minDistance = totalHeight / minHeightFraction;

    if (seconds > maxTime) {
      return;
    }

    if (Math.abs(dy) > Math.abs(dx) && dy > minDistance) {
      onDrag('down');
    } else if (Math.abs(dy) > Math.abs(dx) && -dy > minDistance) {
      onDrag('up');
    }
  };

  return {
    touchStart,
    touchEnd,
  };
}

export function useAsyncProcessor<T>(def: T) {
  const [data, setData] = useState(def);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error>(null);
  const doProcess = useCallback(
    async (f: () => MaybePromise<T>) => {
      try {
        setLoading(true);
        setData(await f());
      } catch (e) {
        setError(e);
      } finally {
        setLoading(false);
      }
    },
    [setData, setError, setLoading],
  );
  return { data, loading, error, setData, doProcess };
}

export function useLoadable<T, A extends any[]>(def: T, f: (...args: A) => MaybePromise<T>) {
  const [data, setData] = useState(def);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error>(null);
  const doLoad = useCallback(
    async (...args: A) => {
      try {
        setLoading(true);
        setData(await f(...args));
      } catch (e) {
        setError(e);
      } finally {
        setLoading(false);
      }
    },
    [setData, setError, setLoading, f],
  );
  return { data, loading, error, setData, doLoad };
}

export function useAsync() {
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState<Error>(null);
  const execute = useCallback(
    async (f: () => any) => {
      try {
        setLoading(true);
        await f();
      } catch (e) {
        setError(e);
      } finally {
        setLoading(false);
      }
    },
    [setError, setLoading],
  );
  return { isLoading, error, execute };
}

export function useAfterNextRender() {
  const queue = useRef<[((value: any) => void), (() => any)][]>([])
  useEffect(() => {
    queue.current.forEach(([resolve, job]) => {
      resolve(job())
    })
    queue.current = []
  })
  return (job: () => any) => {
    return new Promise(resolve => queue.current.push([resolve, job]))
  }
}

export function useStateWatchDefaultValue<S>(initialState: S): [S, Dispatch<SetStateAction<S>>] {
  const [state, setState] = useState(initialState);
  useEffect(() => {
    setState(initialState);
  }, [initialState]);
  return [state, setState];
}

export function renderCustomItemDescription(rn: ReactNode) {
  if (!rn) return null
  return (
    <>
      <br/>
      <b>{rn}</b>
    </>
  )
}

export function getVoices() {
  if (!window.speechSynthesis) {
    return []
  }
  return window.speechSynthesis.getVoices();
}

export function getVoiceId(voice: SpeechSynthesisVoice) {
  return voice.name + ' (' + voice.lang + ')';
}

export function getVoiceForId(vid: string) {
  if (!window.speechSynthesis) {
    return null
  }

  const synth = window.speechSynthesis;
  return (
    synth.getVoices().find((voice) => getVoiceId(voice) === vid) || synth.getVoices().find((voice) => voice.default)
  );
}

export function tts(voice: SpeechSynthesisVoice, text: string, rate: number = 1.0) {
  if (!window.speechSynthesis || !voice) {
    return
  }

  // utter the phrase
  const utterThis = new SpeechSynthesisUtterance(text);
  utterThis.voice = voice;
  utterThis.rate = rate;
  const promise = new Promise(resolve => { utterThis.onend = resolve })
  window.speechSynthesis.speak(utterThis);
  return promise
}

export function executeOnlyOnce(f: (cb: () => any) => MaybePromise<any>) {
  let isLoaded = false;
  let isLoading = false;
  const callbacks: (() => any)[] = []
  return (callback: () => any) => {
    if (isLoaded) {
      // directly return
      callback()
      return
    }

    // add this callback to the queue
    callbacks.push(callback)

    // if this is already loading
    if (isLoading) {
      return;
    }

    isLoading = true;
    f(() => {
      isLoaded = true;
      callbacks.forEach(it => it())
    })
  }
}
